import { Button, Col, Form, Modal, notification, Row, Tooltip } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/Hooks";
import Box from "../../common/components/Box";
import ConfirmModal from "../../common/components/ConfirmModal";
import FloatInput from "../../common/components/FloatInput";
import { setChangePassWarning } from "../../login/redux/LoginSlice";
import { usePrompt } from "../../prompts";
import { useChangePasswordMutation } from "../redux/ProfileApi";

const pattern = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;
const patternMessage = "Nhập mật khẩu tối thiểu 8 ký tự bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt. Mật khẩu mới không quá 5 ký tự trùng với mật khẩu cũ.";

interface ChangePasswordProps {
    visible: boolean,
    onCancel: () => void,
}

const ChangePassword = ({ visible, onCancel }: ChangePasswordProps) => {
    const [form] = Form.useForm();
    const [count, setCount] = useState<any>([]);
    const [changePassword] = useChangePasswordMutation();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isDirty, setIsDirty] = useState(false);
    const [cancelVisible, setCancelVisible] = useState(false);

    const dispatch = useAppDispatch();
    const loginState = useAppSelector(state => state.login);
    const navigate = useNavigate();

    usePrompt(isDirty);

    const onValuesChange = (changedValues: any, allValues: any) => {
        setErrorMessage("");
        let key = Object.keys(changedValues)[0];
        let value = changedValues[key];
        if (typeof (value === "string")) {
            setCount({
                ...count,
                [key]: value.length,
            });
        }

        if (Object.values(allValues)?.some(item => item)) {
            setIsDirty(true);
        } else {
            setIsDirty(false);
        }
    }

    const onFinish = (values: any) => {
        changePassword({
            confirmPass: values.confirmPassword,
            newPass: values.newPassword,
            oldPass: values.oldPassword,
        }).unwrap().then(res => {
            setIsDirty(false);
            notification.success({
                message: "Thay đổi mật khẩu thành công!",
                duration: 5
            });

            // if (visible) {
            //     onCancel();
            // }

            if (loginState.changePassWarning) {
                dispatch(setChangePassWarning(false));
                navigate(0);
            }
        }).catch(err => {
            if (err.data?.message && err.data?.message.toLowerCase().includes("mật khẩu cũ")) {
                setErrorMessage(err.data.message);
            } else {
                notification.error({
                    message: err.data?.message || "Thay đổi mật khẩu thất bại!"
                })
            }
        })
    }

    const onCancelClick = () => {
        if (isDirty) {
            setCancelVisible(true);
        } else {
            onCancel();
        }
    }

    return (
        <Modal
            centered
            visible={visible || loginState.changePassWarning}
            maskClosable={!loginState.changePassWarning}
            onCancel={onCancelClick}
            className={loginState.changePassWarning ? 'custom-modal disable-modal-close' : 'custom-modal'}
            bodyStyle={{ padding: '60px 105px' }}
            style={{ minWidth: 800 }}
            footer={false}
        >
            <div style={{ padding: '20px' }}>
                <h1 style={{ textAlign: 'center', fontSize: 30 }}>Đổi mật khẩu</h1>
                {loginState.changePassWarning ?
                    <Row align="middle" justify="center">
                        <Col span={24} style={{ color: 'red', fontSize: '1em', textAlign: 'center' }}>
                            <div>
                                Tài khoản của bạn đã đến hạn 90 ngày đổi mật khẩu.
                            </div>
                            <div>
                                Vui lòng đổi mật khẩu mới để tiếp tục sử dụng.
                            </div>
                        </Col>
                    </Row> : <></>}

                <Row align="middle" justify="center">
                    <Col span={24}>
                        <Form
                            onFinish={onFinish}
                            className="custom-form validation-form"
                            style={{ marginTop: 25 }}
                            name="personalRegister"
                            form={form}
                            colon={false}
                            onValuesChange={onValuesChange}
                            scrollToFirstError={true}
                        >
                            {/* <Box> */}
                            <Row align="middle" justify="center">
                                <Col xs={24} sm={20}>
                                    <Form.Item
                                        name="oldPassword"
                                        rules={[{ required: true, message: "Vui lòng nhập mật khẩu hiện tại." }]}
                                        status={errorMessage && !errorMessage.includes("Mật khẩu mới") ? "error" : ""}
                                        extra={errorMessage && !errorMessage.includes("Mật khẩu mới") ? <span className="text-danger font-size-12">{errorMessage}</span> : ""}>
                                        <FloatInput label="Mật khẩu hiện tại" placeholder="Mật khẩu hiện tại" maxLength={20} isPassword autoComplete="off" />
                                    </Form.Item>
                                    <label className="input-show-count">{count.oldPassword || 0}/20</label>
                                </Col>
                                <Col xs={24} sm={20}>
                                    <Tooltip title={patternMessage} overlayInnerStyle={{ textAlign: 'center' }}>
                                        <Form.Item
                                            name="newPassword"
                                            rules={[{ required: true, message: "Vui lòng nhập mật khẩu mới." },
                                            { pattern, message: "Mật khẩu không đúng định dạng" }]}
                                            status={errorMessage && errorMessage.includes("Mật khẩu mới") ? "error" : ""}
                                            extra={errorMessage && errorMessage.includes("Mật khẩu mới") ? <span className="text-danger font-size-12">{errorMessage}</span> : ""}
                                        >
                                            <FloatInput label="Mật khẩu mới" placeholder="Mật khẩu mới" maxLength={20} isPassword autoComplete="off" />
                                        </Form.Item>
                                    </Tooltip>
                                    <label className="input-show-count">{count.newPassword || 0}/20</label>
                                </Col>
                                <Col xs={24} sm={20}>
                                    <Form.Item
                                        name="confirmPassword"
                                        rules={[
                                            { required: true, message: "Vui lòng nhập lại mật khẩu mới." },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('newPassword') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Mật khẩu nhập lại không khớp!'));
                                                },
                                            }),]}>
                                        <FloatInput label="Nhập lại mật khẩu mới" placeholder="Nhập lại mật khẩu mới" maxLength={20} isPassword autoComplete="off"/>
                                    </Form.Item>
                                    <label className="input-show-count">{count.confirmPassword || 0}/20</label>
                                </Col>
                            </Row>

                            <Row align="middle" justify="center">
                                <Button
                                    className="button-light-yellow"
                                    style={{ height: 50, width: 240, fontSize: 16 }}
                                    onClick={() => form.submit()}>
                                    ĐỔI MẬT KHẨU
                                </Button>
                            </Row>
                            {/* </Box> */}

                            {cancelVisible ?
                                <ConfirmModal
                                    visible={cancelVisible}
                                    status="error"
                                    onOk={() => onCancel()}
                                    onCancel={() => setCancelVisible(false)}
                                    title={<div style={{ textAlign: 'center' }}>
                                        <div className="font-size-16">Bạn có chắc chắn muốn thoát và không lưu dữ liệu vừa nhập</div>
                                    </div>}
                                /> : <></>}
                        </Form>
                    </Col>
                </Row>
            </div>
        </Modal>
    )
}

export default ChangePassword;